<script setup>
import { computed } from "vue";
import CountyLogoAndTitle from "./CountyLogoAndTitle.vue";
import Services from "./Services.vue";
import { userAuthenticated, useRouterFromInstance } from "@/utils/vue_helpers";
import { getDashboardLinks } from "@/router/dashboard_links";
import { shouldShowNavItem, shouldShowNavParent } from "@/utils/permissions";

const props = defineProps({
  drawerOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:drawerOpen"]);

const innerDrawerOpen = computed({
  get() {
    return props.drawerOpen;
  },
  set(newVal) {
    emit("update:drawerOpen", newVal);
  },
});

const { router, route: currentRoute } = useRouterFromInstance();

function onRouteClicked(route) {
  innerDrawerOpen.value = false;
  router.push(route);
}
</script>

<template>
  <v-navigation-drawer
    v-if="$mobileBreakpoint"
    fixed
    app
    v-model="innerDrawerOpen"
    :width="$mobileBreakpoint ? '100vw' : '264px'"
    :style="{ zIndex: 11 }"
    :clipped="!$mobileBreakpoint && userAuthenticated"
  >
    <template v-if="!userAuthenticated || $mobileBreakpoint">
      <v-list-item>
        <v-list-item-title class="d-flex justify-end">
          <v-btn icon @click="innerDrawerOpen = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-list-item-title>
      </v-list-item>

      <v-list-item>
        <v-list-item-content>
          <v-list-item-title>
            <CountyLogoAndTitle inDrawer />
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider />

      <v-list v-if="userAuthenticated" dense nav>
        <template v-for="(dashboardLink, index) in getDashboardLinks()">
          <!-- Parent item with children — render as expandable group -->
          <v-list-group
            v-if="dashboardLink.children && shouldShowNavParent(dashboardLink)"
            :key="'group-' + index"
            :prepend-icon="dashboardLink.icon"
            :value="dashboardLink.children.some(c => c.route && c.route.name === currentRoute.name)"
          >
            <template #activator>
              <v-list-item-content>
                <v-list-item-title>{{ dashboardLink.text }}</v-list-item-title>
              </v-list-item-content>
            </template>
            <template v-for="(child, childIndex) in dashboardLink.children">
              <v-list-item
                v-if="shouldShowNavItem(child)"
                :key="'child-' + childIndex"
                :input-value="child.route && child.route.name === currentRoute.name"
                @click="onRouteClicked(child.route)"
                class="pl-8"
              >
                <v-list-item-icon>
                  <v-icon>{{ child.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ child.text }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list-group>

          <!-- Leaf item with a direct route -->
          <v-list-item
            v-else-if="!dashboardLink.children && shouldShowNavItem(dashboardLink)"
            :key="'item-' + index"
            :input-value="dashboardLink.route && dashboardLink.route.name === currentRoute.name"
            @click="onRouteClicked(dashboardLink.route)"
          >
            <v-list-item-icon>
              <v-icon>{{ dashboardLink.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title> {{ dashboardLink.text }} </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>

      <v-list dense>
        <v-list-item-group v-if="userAuthenticated">
          <v-list-item>
            <v-list-item-title> General Billing </v-list-item-title>
          </v-list-item>
        </v-list-item-group>

        <Services v-if="$mobileBreakpoint" />
      </v-list>
    </template>
  </v-navigation-drawer>
</template>
